import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { EditorView as EditorViewInstance } from "@codemirror/view";

import { Button } from "./Button";
import { InlineError, Spinner } from "./Feedback";
import { Stack } from "./Layout";

function describedBy(...ids: Array<string | undefined>): string | undefined {
  const value = ids.filter(Boolean).join(" ");
  return value.length > 0 ? value : undefined;
}

function classNames(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

function parseJsonError(value: string): string | null {
  try {
    JSON.parse(value);
    return null;
  } catch (error) {
    if (error instanceof SyntaxError && error.message) {
      return `Invalid JSON: ${error.message}`;
    }

    return "Invalid JSON.";
  }
}

function formatJson(value: string): string | null {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return null;
  }
}

function editorErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Editor unavailable.";
}

export interface CodeEditorProps {
  className?: string;
  description?: ReactNode;
  disabled?: boolean;
  error?: ReactNode;
  formatButtonLabel?: string;
  id?: string;
  label: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  value: string;
}

export function CodeEditor({
  className,
  description,
  disabled = false,
  error,
  formatButtonLabel = "Format JSON",
  id,
  label,
  onChange,
  readOnly = false,
  value,
}: CodeEditorProps) {
  const generatedId = useId();
  const editorId = id ?? generatedId;
  const labelId = `${editorId}-label`;
  const descriptionId = description ? `${editorId}-description` : undefined;
  const validationError = useMemo(() => parseJsonError(value), [value]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const validationErrorId = validationError
    ? `${editorId}-validation-error`
    : undefined;
  const externalErrorId = error ? `${editorId}-error` : undefined;
  const loadErrorId = loadError ? `${editorId}-load-error` : undefined;
  const displayedError = error ?? loadError ?? validationError;
  const displayedErrorId = error
    ? externalErrorId
    : loadError
      ? loadErrorId
      : validationErrorId;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const latestValueRef = useRef(value);
  const viewRef = useRef<EditorViewInstance | null>(null);
  const onChangeRef = useRef(onChange);
  const isSyncingRef = useRef(false);
  const isEditable = !disabled && !readOnly;

  latestValueRef.current = value;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const containerElement = container;

    let cancelled = false;
    let destroyView: (() => void) | undefined;

    setIsEditorReady(false);
    setLoadError(null);

    async function initializeEditor() {
      // CodeMirror is funnelled through a single dynamic import so it lands in
      // its own lazy chunk (mirrors the AudioPlayer/wavesurfer precedent)
      // rather than the always-loaded main app bundle.
      const { createJsonEditor } = await import("./codeEditorModule");

      if (cancelled) {
        return;
      }

      const view = createJsonEditor({
        doc: latestValueRef.current,
        editable: isEditable,
        editorId,
        isSyncing: () => isSyncingRef.current,
        labelId,
        onDocChange: (nextValue) => {
          onChangeRef.current(nextValue);
        },
        parent: containerElement,
      });

      viewRef.current = view;

      destroyView = () => {
        view.destroy();
        if (viewRef.current === view) {
          viewRef.current = null;
        }
      };

      setIsEditorReady(true);
    }

    void initializeEditor().catch((initError: unknown) => {
      if (!cancelled) {
        setLoadError(editorErrorMessage(initError));
        setIsEditorReady(false);
      }
    });

    return () => {
      cancelled = true;
      destroyView?.();
      viewRef.current = null;
    };
  }, [editorId, isEditable, labelId]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    const currentValue = view.state.doc.toString();
    if (currentValue === value) {
      return;
    }

    isSyncingRef.current = true;
    view.dispatch({
      changes: {
        from: 0,
        insert: value,
        to: currentValue.length,
      },
    });
    isSyncingRef.current = false;
  }, [value]);

  useEffect(() => {
    const content = viewRef.current?.contentDOM;
    if (!content) {
      return;
    }

    const ariaDescribedBy = describedBy(descriptionId, displayedErrorId);
    if (ariaDescribedBy) {
      content.setAttribute("aria-describedby", ariaDescribedBy);
    } else {
      content.removeAttribute("aria-describedby");
    }

    if (displayedError) {
      content.setAttribute("aria-invalid", "true");
    } else {
      content.removeAttribute("aria-invalid");
    }

    if (disabled) {
      content.setAttribute("aria-disabled", "true");
    } else {
      content.removeAttribute("aria-disabled");
    }
  }, [
    descriptionId,
    disabled,
    displayedError,
    displayedErrorId,
    isEditorReady,
  ]);

  function handleFormat() {
    const formattedValue = formatJson(value);
    if (formattedValue) {
      onChange(formattedValue);
    }
  }

  return (
    <Stack
      className={classNames(
        "marker-code-editor",
        Boolean(displayedError) && "marker-code-editor--invalid",
        disabled && "marker-code-editor--disabled",
        className,
      )}
      gap={2}
    >
      <div className="marker-code-editor__header">
        <span className="marker-code-editor__label" id={labelId}>
          {label}
        </span>
        <Button
          disabled={!isEditable || Boolean(validationError)}
          onClick={handleFormat}
          size="sm"
          type="button"
          variant="secondary"
        >
          {formatButtonLabel}
        </Button>
      </div>
      <div aria-busy={!isEditorReady} className="marker-code-editor__surface">
        <div className="marker-code-editor__mount" ref={containerRef} />
        {!isEditorReady && !loadError ? (
          <span className="marker-code-editor__loading">
            <Spinner label="Loading editor" />
          </span>
        ) : null}
      </div>
      {description ? (
        <span className="marker-field__description" id={descriptionId}>
          {description}
        </span>
      ) : null}
      {displayedError ? (
        <InlineError id={displayedErrorId}>{displayedError}</InlineError>
      ) : null}
    </Stack>
  );
}
